/**
 * Pure utility functions for Audit History component
 */

export interface User {
  level: number;
  id: string;
}

export const ROLE_TO_LEVEL: { [key: string]: number } = {
  'App User': 1,
  Viewer: 5,
  Creator: 10,
  Publisher: 15,
  'Permission Manager': 17,
  Owner: 20,
  Grantor: 21,
};

/**
 * Convert a Date object to Unix epoch seconds
 */
export const dateToEpoch = (date: Date): number => {
  return Math.floor(date.getTime() / 1000);
};

/**
 * Map numeric user level to role string
 */
export const levelToRole = (level: number): string => {
  if (level >= 21) return 'Grantor';
  if (level >= 20) return 'Owner';
  if (level >= 17) return 'Permission Manager';
  if (level >= 15) return 'Publisher';
  if (level >= 10) return 'Creator';
  if (level >= 5) return 'Viewer';
  if (level >= 1) return 'App User';
  return 'Service Account';
};

/**
 * Format hyphenated strings to title case
 * e.g., "entity-type" -> "Entity Type"
 */
export const formatAuditField = (str: string): string => {
  return str
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
};

/**
 * Map selected roles to corresponding user level IDs
 */
export const mapRolesToLevelIds = (roles: string[], users: User[]): string[] | null => {
  if (roles.length === 0) return [];

  const levelToIds = new Map<number, string[]>();

  for (const user of users) {
    const existing = levelToIds.get(user.level);

    if (existing) {
      existing.push(user.id);
    } else {
      levelToIds.set(user.level, [user.id]);
    }
  }

  const levelIds: string[] = [];

  for (const role of roles) {
    const level = ROLE_TO_LEVEL[role];
    const idsForLevel = levelToIds.get(level);
    if (idsForLevel && idsForLevel.length > 0) {
      levelIds.push(...idsForLevel);
    }
  }

  return levelIds;
};

/**
 * Check if date range is a single day (or less)
 */
export const isDateRangeSingleDay = (start: number, end: number): boolean => {
  return end - start <= 86359;
};
